import FormatCurrency from 'components/Common/FormatCurrency'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabeledSheet from 'LuxKit/LabeledSheet'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { formatInclusionsTitleLuxPlus } from 'constants/inclusions'
import useListTruncator from 'hooks/useListTruncator'
import { isLEOffer } from 'lib/offer/offerTypes'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import LuxPlusLabelWithModal from './LuxPlusLabelWithModal'

const List = styled(VerticalSpacer)`
  padding: ${rem(8)};
`

interface MappedProps {
  subscription: App.ProductSubscription
  isLuxPlusEnabled: boolean
}

type InheritedOfferInclusionItemProps = Pick<ComponentProps<typeof OfferInclusionItem>, 'showMinStayDescription' | 'size' | 'iconSize'>

interface Props extends InheritedOfferInclusionItemProps {
  luxPlusInclusions: Array<App.OfferInclusion>
  accommodationOffer?: App.Offer | App.OfferSummary | App.CruiseOffer | App.Property.Property
  hideUpsellModal?: boolean
  isModalPrimaryActionHidden?: boolean
  /**
   * The number of items to display before the rest are truncated.
   *
   * If set to zero, nothing will be displayed.
   */
  truncateAfter?: number
  onShowMoreClick?: () => void
}

function LuxPlusInclusions({
  luxPlusInclusions,
  showMinStayDescription,
  isLuxPlusEnabled,
  accommodationOffer,
  subscription,
  hideUpsellModal,
  isModalPrimaryActionHidden,
  truncateAfter,
  onShowMoreClick,
  size,
  iconSize,
}: Props & MappedProps) {
  const intl = useIntl()
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(truncateAfter, luxPlusInclusions)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    onShowMoreClick?.()
  }, [onShowMoreClick])

  const subscriptionPrice = <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="rounded" />

  const isAccommodationProperty = !!accommodationOffer && !('type' in accommodationOffer)
  const childOffer = isAccommodationProperty ? undefined : accommodationOffer
  const hotelName = isAccommodationProperty
    ? accommodationOffer.name
    : (isLEOffer(childOffer) ? childOffer.property?.name : '')

  const namedHotelSubTitle = <FormattedMessage
    id="qTzwux-oP9mDbdVR4LJP"
    defaultMessage="Become a member of our VIP travel club for {price} per year to unlock {inclusionsTitle} when you book <bold>{hotelName}</bold>."
    description="Modal subtitle prompting users to join LuxPlus+ with yearly price and specific hotel name"
    values={{
      price: subscriptionPrice,
      inclusionsTitle: formatInclusionsTitleLuxPlus(intl),
      hotelName,
      bold: (chunks) => <b>{chunks}</b>,
    }}
  />

  const genericHotelSubTitle = <FormattedMessage
    id="wxLSxTaI2yX7qD879E-T"
    defaultMessage="Become a member of our VIP travel club for {price} per year to unlock {inclusionsTitle} when you book this hotel."
    description="Modal subtitle prompting users to join LuxPlus+ with yearly price for a generic hotel"
    values={{
      price: subscriptionPrice,
      inclusionsTitle: formatInclusionsTitleLuxPlus(intl),
    }}
  />

  if (!isLuxPlusEnabled || !alwaysVisibleInclusions.length) return null

  return <LabeledSheet
    data-testid="lux-plus-bonus-inclusions"
    kind="lux-plus"
    label={
      <>
        <CSSBreakpoint not="tablet">
          <LuxPlusLabelWithModal
            type="bonus-inclusions"
            inclusions={luxPlusInclusions}
            offer={childOffer}
            hideUpsellModal={hideUpsellModal}
            isModalPrimaryActionHidden={isModalPrimaryActionHidden}
          />
        </CSSBreakpoint>
        <CSSBreakpoint only="tablet">
          <LuxPlusLabelWithModal
            type="only-logo"
            offer={childOffer}
            inclusions={luxPlusInclusions}
            hideUpsellModal={hideUpsellModal}
            isModalPrimaryActionHidden={isModalPrimaryActionHidden}
            modalTitle={intl.formatMessage(
              {
                id: 'vjtOeYjicGVfiGPCP6Zr',
                defaultMessage: 'Join {programName} to enjoy {inclusionsTitle} for this offer and more!',
                description: 'Modal title prompting users to join LuxPlus for bonus inclusions on a specific offer',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME, inclusionsTitle: formatInclusionsTitleLuxPlus(intl) },
            )}
            modalSubTitle={(isAccommodationProperty || isLEOffer(childOffer)) ? namedHotelSubTitle : genericHotelSubTitle}
          />
        </CSSBreakpoint>
      </>
    }
  >
    <List gap={4} data-testid="luxury-plus-inclusions">
      {alwaysVisibleInclusions.map((inclusion) => <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        type="lux-plus"
        showMinStayDescription={showMinStayDescription}
        size={size}
        iconSize={iconSize}
      />)}
      {(onShowMoreClick && !!collapsibleInclusions.length) && <TextLink size="medium" onClick={handleMoreClick}>{intl.formatMessage(
        {
          id: 'PJ3dQ6XMIrnfljKqYNrO',
          defaultMessage: '+{count} more',
          description: 'Link text to show additional hidden LuxPlus bonus inclusions',
        },
        { count: collapsibleInclusions.length },
      )}
      </TextLink>}
    </List>
  </LabeledSheet>
}

export default connect<MappedProps, {}, Props, App.State>((state: App.State) => {
  return {
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    subscription: state.luxPlus.products.subscription,
  }
})(LuxPlusInclusions)
